'use client'

/**
 * HOME SINGLE_REVIEW_CARD PL widget — widget-of-widget child.
 *
 * Renders a single honest-reviews card from its own proto `reviewCard` data — the same v2 nested
 * pattern as the home IRL pair (INFLUENCER_CARD). Routes between the DS card
 * (`ProductReviewCardDs`) and the legacy card (shared `ProductCard` + stars/quote/author footer)
 * via `shouldUseDesignSystem()`, mirroring the two `HonestReviewsV2` forks card-for-card. The
 * section-level values a card's track configs need — tracker, section type/id, tracking metadata,
 * this card's position — live on the parent HONEST_REVIEWS container and arrive via
 * `ReviewCardContext`. Outside that context (no section parent) the card has no section to attach
 * its analytics to, so it renders nothing.
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import cx from 'classnames'

import { ProductReviewCardDs } from '@src/components/blocks/product-review-card-ds'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { ProductCard } from '@src/components/product-card/product-card.component'
import { IProductCard } from '@src/components/product-card/product-card.types'
import { SvdProductCard } from '@src/components/svd-product-card'
import { isPlSvdCard, mapPlSvdCard, type PlSvdCard } from '@src/components/svd-product-card/map-pl-svd-card'
import { RichText as RichTextView } from '@src/quince-components/src'
import { ReviewStars } from '@src/quince-components/src/atoms/reviews/reviewStars/ReviewStars'
import RichTextDomain from '@src/quince-site-common/src/domains/RichText'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import productTileStyles from '@src/SectionRenderer/sections/commonStyles/newHomePageProductTile.module.scss'
import styles from '@src/SectionRenderer/sections/honestReviewsV2/honestReviewsV2.module.scss'
import {
  getContentContainerTrackingMetadata,
  getTrackingContext,
} from '@src/SectionRenderer/sections/productListSection/productListSection.helpers'
import { getFindingMethodAggregate } from '@src/telemetry/findingMethod'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { toRichText } from '../widget-utils'

import { useReviewCardContext } from './review-card-context'

import type { ReviewCardData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

const MAX_STARS = 5

function SingleReviewCardWidgetComponent(props: PLTypedWidgetProps<ReviewCardData>) {
  const reviewCard = props.data.reviewCard
  const ctx = useReviewCardContext()

  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  // TEMPORARY (STRFRNT-15810 · Home SVD preview): render the native SVD card for SVD-shaped cards.
  // `isPlSvdCard` shape-guards on anchor data, so a legacy-shaped card falls through to the legacy
  // ProductCard below. Remove once every PL environment emits the SVD shape unconditionally.
  const isSvdEnabled = useIsSvdEnabled()

  // `product` is a standard PL-enriched ProductCardData — website-product-shaped, consumed through
  // the same one-cast boundary as the shared SINGLE_PRODUCT_CARD leaf.
  const product = (reviewCard?.product ? { product: reviewCard.product } : null) as IProductCard | null
  if (!reviewCard || !product || !ctx) return null

  const { index, tracker, sectionType, sectionId, trackingMetaData } = ctx

  const descriptionRichTextProps = RichTextDomain.makeRichTextProps(toRichText(reviewCard.reviewText))
  const authorRichTextProps = RichTextDomain.makeRichTextProps(toRichText(reviewCard.authorText))

  const trackingContext = getTrackingContext(tracker, product.product.slug)
  const contentContainerTrackingMetadata = getContentContainerTrackingMetadata(trackingMetaData)
  const { reviews: { averageRating = 0 } = {} } = product.product

  const productClickTrackConfig = {
    trackInternal: true,
    trackGA: true,
    ...trackingContext,
    context: {
      sectionType,
      sectionid: sectionId,
      ...trackingMetaData.context,
      productPosition: index,
      ...contentContainerTrackingMetadata,
      findingMethod: getFindingMethodAggregate(),
    },
  }

  const wishlistTrackConfig = {
    ...trackingContext,
    sectionType,
    sectionid: sectionId,
    ...trackingMetaData.context,
    productPosition: index,
    ...contentContainerTrackingMetadata,
    findingMethod: getFindingMethodAggregate(),
  }

  // DS branch — same props the non-PL `HonestReviewsDs.buildReviewCardProps` builds per card.
  if (shouldUseDesignSystem()) {
    return (
      <ProductReviewCardDs
        productCard={product}
        rating={averageRating}
        descriptionContent={descriptionRichTextProps?.content}
        authorContent={authorRichTextProps?.content}
        // @i18n-ignore
        tracker={tracker && `${tracker}__product_carousel`}
        productClickTrackConfig={productClickTrackConfig}
        wishlistTrackConfig={wishlistTrackConfig}
      />
    )
  }

  // Legacy branch — mirrors `HonestReviewsV2Legacy.renderProductReview` (ProductCard with the
  // stars + quote + author custom footer).
  const footer = (
    <div className={styles.footerContainer}>
      <ReviewStars
        className={styles.ratingsContainer}
        classNameIcon={styles.starIcon}
        totalStars={MAX_STARS}
        fillStars={averageRating || 0}
      />
      <RichTextView
        {...descriptionRichTextProps}
        className={cx(styles.reviewDescription, descriptionRichTextProps?.className)}
      />
      <RichTextView {...authorRichTextProps} className={cx(styles.authorText, authorRichTextProps?.className)} />
    </div>
  )

  if (isSvdEnabled && isPlSvdCard(product.product)) {
    return (
      <div className={styles.productCard}>
        {/* `wishlistIconClassName` mirrors the legacy branch: `.wishlistWrapper` contributes
            `margin: -0.5rem`, cancelling the heart button's `padding: 0.5rem !important`. Without it
            the SVD heart sat ~5.5px inboard of the legacy heart on a neighbouring tile.
            `wishlistIconClass` is deliberately NOT wired: `.wishlistIcon` 1.2rem duplicates the
            always-applied `.icon`, so it is a no-op. */}
        <SvdProductCard
          className={cx({ [styles.productCard ?? '']: isDesktop })}
          product={mapPlSvdCard(product.product as unknown as PlSvdCard)}
          wishlistIconClassName={productTileStyles.wishlistWrapper}
          hideTitle
          customFooter={footer}
          allowCustomFooterHover
          wishlistTrackingConfig={wishlistTrackConfig}
          productClickTrackConfig={productClickTrackConfig}
          // Every PL leaf skips the tag refresh — the PL payload is already the fresh one (matches
          // the shared SINGLE_PRODUCT_CARD leaf, `widgets/single-product-card/index.tsx`). This one
          // was missed when that convention was established; confirmed safe to apply here too
          // (STRFRNT-15810).
          skipTagRefresh
        />
      </div>
    )
  }

  return (
    <div className={styles.productCard}>
      <ProductCard
        className={cx({
          [styles.productCard ?? '']: isDesktop,
        })}
        productCard={product}
        // @i18n-ignore
        tracker={tracker && `${tracker}__product_carousel`}
        hideTitle
        productCardImageSectionClass={styles.image}
        customFooter={footer}
        wishlistIconClassName={productTileStyles.wishlistWrapper}
        wishlistIconClass={productTileStyles.wishlistIcon}
        wishlistTrackConfig={wishlistTrackConfig}
        productClickTrackConfig={productClickTrackConfig}
        showMScroll={false}
        allowCustomFooterHover={true}
      />
    </div>
  )
}

export const SingleReviewCardWidget: PLTypedWidgetInterface<ReviewCardData, WidgetType.SINGLE_REVIEW_CARD> = {
  id: WidgetType.SINGLE_REVIEW_CARD,
  errorBoundary: PLBaseErrorBoundary,
  render: SingleReviewCardWidgetComponent,
}
